import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import "./adminUx.css";

export function paginateItems(items, page, pageSize) {
  const list = Array.isArray(items) ? items : [];
  const size = Math.max(1, Number(pageSize) || 20);
  const total = list.length;
  const pageCount = Math.max(1, Math.ceil(total / size) || 1);
  const safePage = Math.min(Math.max(1, Number(page) || 1), pageCount);
  const start = (safePage - 1) * size;
  return {
    total,
    page: safePage,
    pageCount,
    pageSize: size,
    items: list.slice(start, start + size),
  };
}

export function previewText(value, max = 120) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function useAdminQueryParam(key, fallback = "") {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key) ?? fallback;
  const setValue = (next) => {
    const n = new URLSearchParams(searchParams);
    if (next == null || next === "" || String(next) === String(fallback)) n.delete(key);
    else n.set(key, String(next));
    setSearchParams(n, { replace: true });
  };
  return [value, setValue];
}

export function AdminPageHeader({ title, description, actions, extra, testId }) {
  return (
    <div className="admin-page-header" data-testid={testId}>
      <div>
        <h3 className="admin-page-header__title">{title}</h3>
        {description ? <p className="admin-page-header__desc">{description}</p> : null}
        {extra}
      </div>
      {actions ? <div className="admin-page-header__actions">{actions}</div> : null}
    </div>
  );
}

export function AdminSubTabs({ tabs, value, onChange, testId = "admin-subtabs" }) {
  return (
    <div className="admin-subtabs" data-testid={testId} role="tablist">
      {(tabs || []).map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={value === t.id}
          data-testid={t.testId || `admin-subtab-${t.id}`}
          className={`admin-subtab${value === t.id ? " is-on" : ""}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function AdminEmptyState({ message, action }) {
  return (
    <div className="admin-empty" data-testid="admin-empty">
      <div>{message}</div>
      {action ? <div className="admin-empty__action">{action}</div> : null}
    </div>
  );
}

function pageWindow(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const items = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);
  if (start > 2) items.push("ellipsis-start");
  for (let i = start; i <= end; i += 1) items.push(i);
  if (end < pageCount - 1) items.push("ellipsis-end");
  items.push(pageCount);
  return items;
}

export function AdminPagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
  hideIfSingle = true,
}) {
  const pageCount = Math.max(1, Math.ceil((total || 0) / (pageSize || 20)) || 1);
  if (hideIfSingle && total <= pageSize && !onPageSizeChange) return null;
  if (hideIfSingle && total <= (pageSizeOptions?.[0] || pageSize) && total <= pageSize && pageCount <= 1 && !onPageSizeChange) {
    return null;
  }
  if (hideIfSingle && pageCount <= 1 && !onPageSizeChange) return null;

  return (
    <div className="admin-pagination" data-testid="admin-pagination">
      <div className="admin-count">
        총 {total}개 · {page}/{pageCount}페이지
      </div>
      <div className="admin-pagination__pages">
        <button
          type="button"
          className="admin-pagination__btn"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="이전"
        >
          ‹
        </button>
        {pageWindow(page, pageCount).map((item) =>
          typeof item === "number" ? (
            <button
              key={item}
              type="button"
              className={`admin-pagination__btn${item === page ? " is-on" : ""}`}
              onClick={() => onPageChange(item)}
            >
              {item}
            </button>
          ) : (
            <span key={item} className="admin-pagination__btn" style={{ border: 0, minWidth: "1.25rem" }}>
              …
            </span>
          )
        )}
        <button
          type="button"
          className="admin-pagination__btn"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          aria-label="다음"
        >
          ›
        </button>
        {onPageSizeChange ? (
          <select
            className="admin-filter"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="페이지 크기"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}개
              </option>
            ))}
          </select>
        ) : null}
      </div>
    </div>
  );
}

export function AdminEditDrawer({ open, title, onClose, footer, children, testId = "admin-edit-drawer" }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="admin-drawer" data-testid={testId}>
      <button type="button" className="admin-drawer__backdrop" aria-label="닫기" onClick={onClose} />
      <aside className="admin-drawer__panel" role="dialog" aria-modal="true" aria-label={title}>
        <div className="admin-drawer__head">
          <h4 className="admin-drawer__title">{title}</h4>
          <button type="button" onClick={onClose} aria-label="닫기" className="admin-row__btn">
            <X size={14} />
          </button>
        </div>
        <div className="admin-drawer__body">{children}</div>
        {footer ? <div className="admin-drawer__footer">{footer}</div> : null}
      </aside>
    </div>
  );
}

export function AdminField({ label, hint, children }) {
  return (
    <div className="admin-field">
      {label ? <label>{label}</label> : null}
      {children}
      {hint ? <p className="admin-field__hint">{hint}</p> : null}
    </div>
  );
}

export function useClientList(items, { pageSize = 20, resetKey = "" } = {}) {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(pageSize);

  useEffect(() => {
    setPage(1);
  }, [resetKey, pageSize]);

  const paged = useMemo(() => paginateItems(items, page, size), [items, page, size]);

  useEffect(() => {
    if (page !== paged.page) setPage(paged.page);
  }, [page, paged.page]);

  return {
    ...paged,
    setPage,
    setPageSize: (next) => {
      setSize(next);
      setPage(1);
    },
  };
}
